import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import { collection, addDoc, onSnapshot, doc, updateDoc, deleteDoc, query, orderBy } from "firebase/firestore";
import useClients from "./useClients";

const SERVICE_OPTIONS = ["Bookkeeping","Tax Preparation","CFO Advisory","Bookkeeping + CFO Bundle","Full Service","Cleanup / Catchup","QBO Setup","One-time Project"];
const STATUS_COLORS = {Paid:["#EAF3DE","#3B6D11"],Sent:["#E6F1FB","#185FA5"],Overdue:["#FCEBEB","#A32D2D"],Draft:["#f5f5f0","#888"]};

const S = {
  card:{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,padding:"16px 20px",marginBottom:16},
  btn:{fontSize:12,padding:"5px 13px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnPrimary:{fontSize:12,padding:"5px 13px",borderRadius:8,border:"none",background:"#C0111F",color:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  input:{width:"100%",padding:"6px 9px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",background:"#fff",boxSizing:"border-box"},
  th:{textAlign:"left",padding:"6px 10px",borderBottom:"0.5px solid #e0dfd8",color:"#888",fontWeight:500,fontSize:12},
  td:{padding:"8px 10px",borderBottom:"0.5px solid #f0efe8",verticalAlign:"middle",fontSize:13},
  badge:(bg,c)=>({display:"inline-block",fontSize:10,padding:"2px 8px",borderRadius:20,background:bg,color:c,fontWeight:500}),
};

export default function Invoicing({ assignedClients }){
  const { clientNames: CLIENT_NAMES } = useClients(assignedClients);
  const [invoices,setInvoices]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showAdd,setShowAdd]=useState(false);
  const [filterClient,setFilterClient]=useState("All Clients");
  const [form,setForm]=useState({client:"",service:"",amount:"",due:"",status:"Draft"});
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));

  useEffect(()=>{
    const q=query(collection(db,"invoices"),orderBy("createdAt","desc"));
    const unsub=onSnapshot(q,snap=>{
      setInvoices(snap.docs.map(d=>({...d.data(),firestoreId:d.id})));
      setLoading(false);
    },err=>{ console.error(err); setLoading(false); });
    return ()=>unsub();
  },[]);

  const addInvoice=async()=>{
    if(!form.client||!form.amount) return;
    const num="#"+String(1000+invoices.length+1);
    try{
      await addDoc(collection(db,"invoices"),{...form,num,createdAt:new Date().toISOString()});
      setForm({client:"",service:"",amount:"",due:"",status:"Draft"});
      setShowAdd(false);
    }catch(e){ alert("Failed to save: "+e.message); }
  };

  const updateStatus=async(firestoreId,status)=>{
    try{ await updateDoc(doc(db,"invoices",firestoreId),{status}); }catch(e){}
  };

  const filtered=invoices.filter(i=>filterClient==="All Clients"||i.client===filterClient);
  const total=invoices.reduce((s,i)=>s+parseFloat((i.amount||"0").replace(/[^0-9.]/g,"")||0),0);
  const collected=invoices.filter(i=>i.status==="Paid").reduce((s,i)=>s+parseFloat((i.amount||"0").replace(/[^0-9.]/g,"")||0),0);
  const overdue=invoices.filter(i=>i.status==="Overdue").reduce((s,i)=>s+parseFloat((i.amount||"0").replace(/[^0-9.]/g,"")||0),0);

  if(loading) return <div style={{textAlign:"center",padding:"40px 0",color:"#888",fontSize:13}}>Loading invoices...</div>;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{fontSize:14,fontWeight:500}}>Invoicing & Billing</div>
        <div style={{display:"flex",gap:8}}>
          <select style={{...S.input,width:220}} value={filterClient} onChange={e=>setFilterClient(e.target.value)}>
            <option>All Clients</option>
            {CLIENT_NAMES.map(c=><option key={c}>{c}</option>)}
          </select>
          <button style={S.btnPrimary} onClick={()=>setShowAdd(true)}>+ New Invoice</button>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
        {[["Total Invoiced","$"+total.toLocaleString(),"#185FA5"],["Collected","$"+collected.toLocaleString(),"#3B6D11"],["Overdue","$"+overdue.toLocaleString(),"#A32D2D"],["Drafts",invoices.filter(i=>i.status==="Draft").length,"#854F0B"]].map(([l,v,c])=>(
          <div key={l} style={{background:"#f5f5f0",borderRadius:8,padding:"14px 16px"}}>
            <div style={{fontSize:12,color:"#888",marginBottom:6}}>{l}</div>
            <div style={{fontSize:22,fontWeight:500,color:c}}>{v}</div>
          </div>
        ))}
      </div>

      {showAdd&&(
        <div style={{...S.card,border:"1px solid #C0111F22",background:"#fdf9f9",marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:500,marginBottom:12}}>New Invoice</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Client</div>
              <select style={S.input} value={form.client} onChange={e=>set("client",e.target.value)}>
                <option value="">Select client...</option>
                {CLIENT_NAMES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Service</div>
              <select style={S.input} value={form.service} onChange={e=>set("service",e.target.value)}>
                <option value="">Select service...</option>
                {SERVICE_OPTIONS.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Amount</div><input style={S.input} value={form.amount} onChange={e=>set("amount",e.target.value)} placeholder="e.g. $350"/></div>
            <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Due date</div><input style={S.input} type="date" value={form.due} onChange={e=>set("due",e.target.value)}/></div>
            <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Status</div>
              <select style={S.input} value={form.status} onChange={e=>set("status",e.target.value)}>
                <option>Draft</option><option>Sent</option><option>Paid</option><option>Overdue</option>
              </select>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button style={S.btnPrimary} onClick={addInvoice}>Create Invoice</button>
            <button style={S.btn} onClick={()=>setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={S.card}>
        {filtered.length===0
          ?<div style={{fontSize:13,color:"#aaa",textAlign:"center",padding:"32px 0"}}>{invoices.length===0?"No invoices yet. Click + New Invoice to create your first one.":"No invoices for "+filterClient+"."}</div>
          :<table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr>
              <th style={S.th}>#</th><th style={S.th}>Client</th><th style={S.th}>Service</th>
              <th style={S.th}>Amount</th><th style={S.th}>Due</th><th style={S.th}>Status</th><th style={S.th}></th>
            </tr></thead>
            <tbody>
              {filtered.map(inv=>(
                <tr key={inv.firestoreId}>
                  <td style={{...S.td,color:"#888"}}>{inv.num}</td>
                  <td style={S.td}>{inv.client}</td>
                  <td style={{...S.td,color:"#888"}}>{inv.service}</td>
                  <td style={{...S.td,fontWeight:500}}>{inv.amount}</td>
                  <td style={{...S.td,color:"#888"}}>{inv.due||"—"}</td>
                  <td style={S.td}><span style={S.badge(...(STATUS_COLORS[inv.status]||["#f5f5f0","#888"]))}>{inv.status}</span></td>
                  <td style={S.td}>
                    <select style={{fontSize:11,padding:"2px 6px",borderRadius:6,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer"}}
                      value={inv.status} onChange={e=>updateStatus(inv.firestoreId,e.target.value)}>
                      <option>Draft</option><option>Sent</option><option>Paid</option><option>Overdue</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
}
